import { useCallback, useEffect } from "react";
import { AnalyticsEvents, getAnalyticsProvider, useAnalytics } from "../../features/analytics";
import { useApp } from "../../features/app";
import appLogger from "../../infra/logger/appLogger";
import { setDeviceType } from "../app/appSlice";
import { useAppDispatch, useAppSelector } from "../hook";
import { SubscriptionTiers, useUserInfo } from "../user";
import { audienceChanged, fetchBannerDetails, invalidateBanners, setClosedBanners, setViewedBanners } from "./cmsSlice";
import { BannerType } from "./types";
import { addBannersToStorage, getBannerSegment, getBannersFromLocalStorage, getUnseenSessionBanners, getUpdatedViewedBanner, getUserPropertiesPayload, } from "./utils";
const cmsLogger = appLogger.withTag("Cms");
let isBannerProcessLocked = false;
let bannerCallInProgress = false;
let persistentPreviewBannerName = undefined;
export default function useCms(previewBannerName) {
    const { subscriptionTier, billingCycle, isAuthLoaded, isAuthenticated, userProfile, onlyUserId } = useUserInfo();
    const dispatch = useAppDispatch();
    const cmsStates = useAppSelector((state) => state.cms);
    const { isPartner, applicationId } = useApp();
    const { logEvent } = useAnalytics();
    const { isLoading, areBannersLoaded, bannerDetails, viewedBanners, closedBanners } = cmsStates;
    persistentPreviewBannerName = previewBannerName;
    const getBannerDetails = async (bannerNames, preview = false) => {
        if (isLoading || areBannersLoaded || bannerCallInProgress) {
            return;
        }
        try {
            bannerCallInProgress = true;
            await dispatch(fetchBannerDetails({
                bannerNames: JSON.stringify(bannerNames),
                preview,
                isSignedIn: !!onlyUserId,
                segment: getBannerSegment(subscriptionTier, billingCycle),
                applicationId,
                audienceGeneration: cmsStates.audienceGeneration,
            })).unwrap();
        }
        catch (error) {
            cmsLogger.error(error);
        }
        finally {
            isBannerProcessLocked = false;
            bannerCallInProgress = false;
        }
    };
    const setDevice = useCallback((deviceType) => {
        dispatch(setDeviceType(deviceType));
    }, []);
    const addUserPropertiesToAnalytics = async () => {
        try {
            const payload = getUserPropertiesPayload(isAuthenticated, subscriptionTier, billingCycle);
            getAnalyticsProvider()?.setUserProperties(payload);
        }
        catch (err) {
            cmsLogger.error(err);
        }
    };
    const triggerBannerFetch = async () => {
        try {
            if (isBannerProcessLocked || !isAuthLoaded || persistentPreviewBannerName || isPartner) {
                return;
            }
            if (isAuthenticated && !userProfile) {
                return;
            }
            // Claim the lock only when the request will actually go out. A run that claims it and then
            // declines to fetch keeps holding it until its `finally` executes a microtask later, and the
            // commit that invalidates the banners can land inside that window — so the run that should
            // have fetched for the new audience sees a held lock and returns. Nothing changes afterwards,
            // so no fetch is ever made and the previous audience's banners stay on screen. That is why
            // this repeats getBannerDetails' own precondition rather than relying on it: the check has to
            // happen before the lock is taken, not inside the call it guards.
            if (isLoading || areBannersLoaded || bannerCallInProgress) {
                return;
            }
            isBannerProcessLocked = true;
            addUserPropertiesToAnalytics();
            await getBannerDetails();
        }
        catch (err) {
            cmsLogger.error(err);
        }
        finally {
            isBannerProcessLocked = false;
        }
    };
    const onUserViewedBanners = useCallback((banners = [], contentfulScreen) => {
        const newlyViewedBanners = getUnseenSessionBanners(viewedBanners, banners);
        if (!banners?.length || !contentfulScreen || !newlyViewedBanners?.length)
            return;
        newlyViewedBanners.forEach((banner) => logEvent(AnalyticsEvents.BANNER_VIEW, {
            user_id: onlyUserId || null,
            banner_name: banner?.name,
            subscription_tier: onlyUserId ? subscriptionTier : SubscriptionTiers.ANONYMOUS,
            page_location: contentfulScreen,
        }));
        const updatedViewedBanners = getUpdatedViewedBanner(viewedBanners, newlyViewedBanners);
        dispatch(setViewedBanners(updatedViewedBanners));
    }, [viewedBanners.length]);
    const onUserClosedBanner = async (banners = [], screen, placement) => {
        if (placement !== BannerType.PopUp) {
            const bannerNames = banners?.filter((banner) => banner.closable)?.map((banner) => banner.name);
            dispatch(setClosedBanners({
                screen,
                placement: placement,
                bannerNames,
            }));
            return;
        }
        const filterPopUpBanners = banners?.filter((banner) => banner?.placement === BannerType.PopUp);
        await addBannersToStorage(filterPopUpBanners);
    };
    const onUserCTAClick = async (banner, action, screen) => {
        if (!banner || !screen)
            return;
        logEvent(AnalyticsEvents.BANNER_CTA_CLICK, {
            banner_name: banner.name,
            cta_label: action.label,
            page_location: screen,
        });
    };
    const getBannersToShow = async (contentFulScreen, placement) => {
        const bannersGroupedByScreen = bannerDetails?.[contentFulScreen];
        const banners = bannersGroupedByScreen?.[placement];
        if (!banners)
            return [];
        if (placement !== BannerType.PopUp)
            return banners;
        const locallyStoredBanners = await getBannersFromLocalStorage();
        const bannersToShow = banners?.filter((banner) => {
            const hasBannerUpdated = locallyStoredBanners[banner?.name] !== banner?.version;
            return hasBannerUpdated;
        });
        return bannersToShow;
    };
    const getInlineBannersRowMapping = useCallback((screenName) => {
        if (!screenName)
            return {};
        const screenBanners = bannerDetails?.[screenName];
        const inLineBanners = screenBanners?.[BannerType.Inline];
        if (!inLineBanners?.length)
            return {};
        const rowMapping = {};
        inLineBanners.forEach((item) => {
            if (!item?.inlineBannerRowIndex) {
                return;
            }
            const bannersAtRowIndex = rowMapping[item.inlineBannerRowIndex] || [];
            bannersAtRowIndex.push(item);
            rowMapping[item.inlineBannerRowIndex] = bannersAtRowIndex;
        });
        return rowMapping;
    }, [bannerDetails]);
    const hideClosedBanners = (contentFulScreen, placement, banners) => {
        const closedBannerNames = closedBanners?.[contentFulScreen]?.[placement] || [];
        const filteredBanners = banners?.filter((banner) => !closedBannerNames.includes(banner?.name));
        return filteredBanners;
    };
    const onUserSegmentChanged = useCallback(() => {
        // Release the in-flight guards as well as the loaded flag. A fetch started for the previous
        // audience cannot be cancelled, so the new one has to be allowed to start alongside it; the
        // stale response is discarded on arrival because its audience generation no longer matches.
        isBannerProcessLocked = false;
        bannerCallInProgress = false;
        dispatch(invalidateBanners());
    }, [dispatch]);
    // Every mounted useCms reports the audience it is showing banners for. The store decides whether
    // that is a change, so the reset happens once however many components are mounted.
    const audienceKey = onlyUserId ? getBannerSegment(subscriptionTier, billingCycle) : "anonymous";
    useEffect(() => {
        // A fetch already running belongs to the previous audience and cannot be cancelled, so release
        // the guards and let the new one start alongside it; the stale response is discarded on arrival.
        isBannerProcessLocked = false;
        bannerCallInProgress = false;
        dispatch(audienceChanged(audienceKey));
    }, [audienceKey, dispatch]);
    // audienceGeneration and areBannersLoaded are dependencies so that invalidating the banners
    // actually runs the fetch again: the audience fields settle in the same commit that would
    // otherwise have been the only chance to refetch.
    //
    // hasDiscardedResponse covers the case where the audience changes while a fetch is still in
    // flight. getBannerDetails declines to start a second one while that request is open, and the
    // response is thrown away on arrival because its audience no longer matches — so without a signal
    // here nothing would ever fetch for the new audience, and the previous audience's banners would
    // stay on screen until something remounted the hook. The signal is deliberately "a response was
    // discarded" rather than "loading finished": a *failed* request also finishes loading, and
    // retrying on that would repeat for as long as the API errors, with no backoff.
    useEffect(() => {
        if (persistentPreviewBannerName) {
            getBannerDetails([persistentPreviewBannerName], true);
            return;
        }
        triggerBannerFetch();
    }, [
        isAuthLoaded,
        userProfile,
        isAuthenticated,
        subscriptionTier,
        billingCycle,
        cmsStates.audienceGeneration,
        areBannersLoaded,
        cmsStates.hasDiscardedResponse,
    ]);
    return {
        getBannerDetails,
        bannerDetails,
        isLoading,
        setDevice,
        areBannersLoaded,
        viewedBanners,
        onUserViewedBanners,
        onUserClosedBanner,
        getBannersToShow,
        onUserCTAClick,
        getInlineBannersRowMapping,
        closedBanners,
        hideClosedBanners,
        onUserSegmentChanged,
    };
}
